import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { NavLink } from 'react-router-dom';
import type { StylistProfile, StylistThread } from '../../shared/stylist';
import {
  askStylist,
  deleteStylistProfile,
  deleteStylistThread,
  getStylist,
  saveStylistProfile,
} from '../lib/stylist';
import { Icon } from './Icon';

const STARTERS = [
  'Make me a polished work outfit',
  'What can I wear for a romantic dinner?',
  'Style something comfortable for travel',
  'Build a playful everyday look',
];

function Conversation({
  thread,
  sending,
  onSend,
}: {
  thread: StylistThread | null;
  sending: boolean;
  onSend: (message: string) => Promise<void>;
}) {
  const [message, setMessage] = useState('');
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [thread?.messages.length]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = message.trim();
    if (!value) return;
    setMessage('');
    await onSend(value);
  }

  return (
    <section className="stylist-chat">
      <div className="stylist-chat__heading">
        <span className="brand-mark">
          <Icon name="sparkles" size={24} />
        </span>
        <div>
          <h2>{thread?.title ?? 'Start a styling conversation'}</h2>
          <p>Every recommended card is an item already inside your closet.</p>
        </div>
      </div>
      <div aria-live="polite" className="stylist-messages">
        {!thread?.messages.length ? (
          <div className="stylist-welcome">
            <p className="eyebrow">Ask naturally</p>
            <h2>What would you like to wear?</h2>
            <p>
              Mention an occasion, feeling, colour, or an item by name. Follow up with requests like
              “keep this but swap the shoes.”
            </p>
            <div>
              {STARTERS.map((starter) => (
                <button
                  disabled={sending}
                  key={starter}
                  type="button"
                  onClick={() => void onSend(starter)}
                >
                  {starter}
                </button>
              ))}
            </div>
          </div>
        ) : (
          thread.messages.map((entry) => (
            <article className={`stylist-message is-${entry.role}`} key={entry.id}>
              <span>{entry.role === 'assistant' ? 'Stylist' : 'You'}</span>
              <p>{entry.content}</p>
              {entry.garments.length ? (
                <div className="stylist-garments">
                  {entry.garments.map((garment) => (
                    <NavLink key={garment.id} to={`/closet/${garment.id}`}>
                      <img alt="" src={garment.photoUrl} />
                      <span>
                        <strong>{garment.name}</strong>
                        <small>
                          {garment.category} · {garment.primaryColor}
                        </small>
                      </span>
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </article>
          ))
        )}
        {sending ? (
          <div className="stylist-typing" role="status">
            <span />
            <span />
            <span />
            Checking your real closet…
          </div>
        ) : null}
        <div ref={end} />
      </div>
      <form className="stylist-composer" onSubmit={(event) => void submit(event)}>
        <label className="sr-only" htmlFor="stylist-message">
          Ask your stylist
        </label>
        <textarea
          id="stylist-message"
          maxLength={800}
          placeholder="e.g. Keep this look, but swap the shoes…"
          rows={2}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <button
          aria-label="Send styling request"
          className="button button--primary"
          disabled={sending || !message.trim()}
          type="submit"
        >
          <Icon name="arrow" size={19} />
        </button>
      </form>
    </section>
  );
}

function PersonalProfile({
  profile,
  saving,
  onSave,
  onDelete,
}: {
  profile: StylistProfile;
  saving: boolean;
  onSave: (palette: string[], preferences: string, enabled: boolean) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [palette, setPalette] = useState(profile.colorPalette.join(', '));
  const [preferences, setPreferences] = useState(profile.fitPreferences ?? '');
  const [enabled, setEnabled] = useState(profile.bodyGuidanceEnabled);

  useEffect(() => {
    setPalette(profile.colorPalette.join(', '));
    setPreferences(profile.fitPreferences ?? '');
    setEnabled(profile.bodyGuidanceEnabled);
  }, [profile]);

  return (
    <aside className="stylist-profile">
      <div>
        <p className="eyebrow">Optional and private</p>
        <h2>Personal style profile</h2>
        <p>
          Save only preferences you want the stylist to use. No photo or body analysis happens here.
        </p>
      </div>
      <label className="field">
        <span>
          Favourite palette <small>comma separated</small>
        </span>
        <input
          maxLength={300}
          placeholder="Navy, rose, cream"
          value={palette}
          onChange={(event) => setPalette(event.target.value)}
        />
      </label>
      <label className="profile-consent">
        <input
          checked={enabled}
          type="checkbox"
          onChange={(event) => setEnabled(event.target.checked)}
        />
        <span>
          <strong>Use my positive fit preferences</strong>
          <small>This is off until you choose it.</small>
        </span>
      </label>
      {enabled ? (
        <label className="field">
          <span>What feels good to you?</span>
          <textarea
            maxLength={500}
            placeholder="e.g. I enjoy relaxed silhouettes and defined shoulders."
            rows={4}
            value={preferences}
            onChange={(event) => setPreferences(event.target.value)}
          />
        </label>
      ) : null}
      <button
        className="button button--secondary button--wide"
        disabled={saving}
        type="button"
        onClick={() =>
          void onSave(
            palette
              .split(',')
              .map((color) => color.trim())
              .filter(Boolean),
            preferences,
            enabled,
          )
        }
      >
        {saving ? 'Saving…' : 'Save personal profile'}
      </button>
      {profile.updatedAt ? (
        <button
          className="text-button text-button--danger"
          disabled={saving}
          type="button"
          onClick={() => void onDelete()}
        >
          Delete personal profile
        </button>
      ) : null}
    </aside>
  );
}

export function StylistPage() {
  const [threads, setThreads] = useState<StylistThread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [profile, setProfile] = useState<StylistProfile>({
    colorPalette: [],
    fitPreferences: null,
    bodyGuidanceEnabled: false,
    updatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState('');
  const active = threads.find((thread) => thread.id === activeId) ?? null;

  useEffect(() => {
    void getStylist()
      .then((result) => {
        setThreads(result.threads);
        setActiveId(result.threads[0]?.id ?? null);
        setProfile(result.profile);
      })
      .catch((caught: unknown) =>
        setMessage(caught instanceof Error ? caught.message : 'Your stylist could not load.'),
      )
      .finally(() => setLoading(false));
  }, []);

  async function send(value: string) {
    setSending(true);
    setMessage('');
    try {
      const thread = await askStylist(value, active?.id);
      setThreads((current) => [thread, ...current.filter((entry) => entry.id !== thread.id)]);
      setActiveId(thread.id);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The stylist could not reply.');
    } finally {
      setSending(false);
    }
  }

  async function removeConversation() {
    if (!active) return;
    // Browser dialogs are blocked in installed/embedded app contexts, so
    // destructive actions confirm with an explicit second tap instead.
    if (confirming !== `thread-${active.id}`) {
      setConfirming(`thread-${active.id}`);
      setMessage('Tap Delete conversation again to permanently remove it.');
      return;
    }
    setConfirming('');
    setMessage('');
    try {
      await deleteStylistThread(active.id);
      setThreads((current) => {
        const remaining = current.filter((thread) => thread.id !== active.id);
        setActiveId(remaining[0]?.id ?? null);
        return remaining;
      });
      setMessage('Styling conversation deleted.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The conversation could not delete.');
    }
  }

  async function saveProfile(palette: string[], preferences: string, enabled: boolean) {
    setSavingProfile(true);
    setMessage('');
    try {
      setProfile(
        await saveStylistProfile({
          colorPalette: palette,
          ...(preferences.trim() ? { fitPreferences: preferences } : {}),
          bodyGuidanceEnabled: enabled,
        }),
      );
      setMessage('Personal style profile saved.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The profile could not save.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function removeProfile() {
    if (confirming !== 'profile') {
      setConfirming('profile');
      setMessage('Tap Delete profile again to remove your palette and fit preferences.');
      return;
    }
    setConfirming('');
    setSavingProfile(true);
    setMessage('');
    try {
      setProfile(await deleteStylistProfile());
      setMessage('Personal style profile deleted independently.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'The profile could not delete.');
    } finally {
      setSavingProfile(false);
    }
  }

  if (loading)
    return (
      <div className="page">
        <p>Opening your private stylist…</p>
      </div>
    );

  return (
    <div className="page stylist-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Your private wardrobe assistant</p>
          <h1>Stylist</h1>
          <p>Ask naturally. Every suggested piece is selected from your real closet first.</p>
        </div>
        <button
          className="button button--secondary"
          type="button"
          onClick={() => setActiveId(null)}
        >
          New conversation
        </button>
      </header>
      {message ? <p className="stylist-notice">{message}</p> : null}
      <div className="stylist-layout">
        <div>
          {threads.length ? (
            <div className="thread-tabs" aria-label="Stylist conversations">
              {threads.slice(0, 6).map((thread) => (
                <button
                  aria-pressed={thread.id === activeId}
                  className={thread.id === activeId ? 'is-active' : ''}
                  key={thread.id}
                  type="button"
                  onClick={() => setActiveId(thread.id)}
                >
                  {thread.title}
                </button>
              ))}
            </div>
          ) : null}
          <Conversation sending={sending} thread={active} onSend={send} />
          {active ? (
            <button
              className="text-button text-button--danger delete-thread"
              type="button"
              onClick={() => void removeConversation()}
            >
              {confirming === `thread-${active.id}`
                ? 'Tap again to confirm'
                : 'Delete this conversation'}
            </button>
          ) : null}
        </div>
        <PersonalProfile
          profile={profile}
          saving={savingProfile}
          onDelete={removeProfile}
          onSave={saveProfile}
        />
      </div>
    </div>
  );
}
